import { reaction } from "mobx";
export class OverlayFocusManager {
    constructor(stack) {
        this.stack = stack;
        this.savedFocus = new Map();
        this.previousDepth = 0;
        this.pendingRestore = null;
        this.disposer = null;
        /**
         * Modal finished hiding — restore focus immediately.
         * If a parent modal will re-show (stack non-empty), its children are
         * currently unmounted so the selector won't match. Keep pendingRestore
         * alive for handleModalShown.
         */
        this.handleModalHidden = () => {
            const saved = this.pendingRestore;
            if (saved === null)
                return;
            // Only apply the viewport guard when returning to the page (stack empty).
            // Inside modals the scroll is reset to top on re-show, so the element may
            // be off-screen even though it exists and should receive focus.
            const checkViewport = this.stack.size === 0;
            if (restoreFocus(saved, checkViewport)) {
                if (this.stack.size === 0) {
                    this.pendingRestore = null;
                }
            }
        };
        /**
         * Modal finished showing — restore focus after Bootstrap's focus trap.
         * The parent modal has re-mounted its children, so the selector should match.
         */
        this.handleModalShown = () => {
            const saved = this.pendingRestore;
            if (saved === null)
                return;
            this.pendingRestore = null;
            restoreFocus(saved, false);
        };
    }
    start() {
        this.previousDepth = this.stack.size;
        this.disposer = reaction(() => this.stack.size, (newDepth) => {
            var _a;
            const prevDepth = this.previousDepth;
            this.previousDepth = newDepth;
            if (newDepth > prevDepth) {
                // If we have a pendingRestore for this depth, it means we just
                // popped and immediately pushed (replace via pop+push). Preserve
                // the original saved focus instead of capturing the current
                // (likely stale) active element. Example: Menu -> "add to cart" click -> Add to cart modal -> "add to cart" click -> Pop state -> Added to Cart modal -> Pop state -> Back to Menu with focus still on the clicked Menu "Add to cart" button.
                if (this.pendingRestore) {
                    this.savedFocus.set(prevDepth, this.pendingRestore);
                    this.pendingRestore = null;
                }
                else {
                    this.savedFocus.set(prevDepth, saveActiveElement());
                }
            }
            else if (newDepth < prevDepth) {
                this.pendingRestore = (_a = this.savedFocus.get(newDepth)) !== null && _a !== void 0 ? _a : null;
                // Clean up entries at depths we've left
                for (const d of this.savedFocus.keys()) {
                    if (d >= newDepth)
                        this.savedFocus.delete(d);
                }
            }
        });
        document.addEventListener("hidden.bs.modal", this.handleModalHidden);
        document.addEventListener("shown.bs.modal", this.handleModalShown);
    }
    stop() {
        var _a;
        (_a = this.disposer) === null || _a === void 0 ? void 0 : _a.call(this);
        this.disposer = null;
        document.removeEventListener("hidden.bs.modal", this.handleModalHidden);
        document.removeEventListener("shown.bs.modal", this.handleModalShown);
        this.savedFocus.clear();
        this.pendingRestore = null;
    }
}
/** Save the active element reference and a selector based on anchor id + data-field-id. */
function saveActiveElement() {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement) || el === document.body) {
        return { element: null, selector: null };
    }
    // Find closest ancestor with an id (including self)
    const anchor = el.closest("[id]");
    const anchorPart = (anchor === null || anchor === void 0 ? void 0 : anchor.id) ? `#${CSS.escape(anchor.id)}` : "";
    // Use the element's own data-field-id
    const fieldId = el.getAttribute("data-field-id");
    const fieldPart = fieldId ? `[data-field-id="${CSS.escape(fieldId)}"]` : "";
    const selector = `${anchorPart} ${fieldPart}`.trim() || null;
    return { element: el, selector };
}
/** Try to focus the element, falling back to its dropdown toggle if it's not focusable. */
function tryFocusWithFallback(el, checkViewport) {
    if (tryFocus(el, checkViewport))
        return true;
    const toggle = findDropdownToggle(el);
    return toggle ? tryFocus(toggle, checkViewport) : false;
}
/** Try to restore focus */
function restoreFocus(saved, checkViewport) {
    var _a;
    // Try the saved element reference first (works if it's still in the DOM)
    if (((_a = saved.element) === null || _a === void 0 ? void 0 : _a.isConnected) &&
        tryFocusWithFallback(saved.element, checkViewport)) {
        return true;
    }
    // Fall back to selector (for modals, where the original element is likely disconnected because of unmounting or re-rendering)
    if (saved.selector) {
        const el = document.querySelector(saved.selector);
        if (el && tryFocusWithFallback(el, checkViewport))
            return true;
    }
    return false;
}
/** Attempt to focus an element and verify it succeeded.
 *
 * When `checkViewport` is true, skips elements outside the viewport to avoid
 * the page jumping when the previously-focused element is no longer visible
 * (e.g. user scrolled down, a bottom-sheet opened, and restoring focus would
 * scroll back to the top). Only applied when returning to the page (depth 0),
 * not when switching between modals where scroll resets to top. (Task: B2C-1357)
 */
function tryFocus(el, checkViewport) {
    if (checkViewport) {
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.bottom > 0 &&
            rect.top < window.innerHeight &&
            rect.right > 0 &&
            rect.left < window.innerWidth;
        if (!isInViewport)
            return false;
    }
    el.focus();
    return document.activeElement === el;
}
/** Given an element inside a `.dropdown-menu`, find the sibling `.dropdown-toggle` button. */
function findDropdownToggle(el) {
    var _a, _b;
    const menu = el.closest(".dropdown-menu");
    return ((_b = (_a = menu === null || menu === void 0 ? void 0 : menu.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector(".dropdown-toggle")) !== null && _b !== void 0 ? _b : null);
}
